import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  Container,
} from '@mui/material';
import {
  Menu as MenuIcon,
  School as SchoolIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import WeChatQRDialog from '../common/WeChatQRDialog';
import useWeChatQR from '../../hooks/useWeChatQR';
import UserStatus from '../common/UserStatus';
import MultiLoginDialog from '../common/MultiLoginDialog';

const navigationItems = [
  { label: '首页', path: '/' },
  { label: '院校库', path: '/universities' },
  { label: '专业搜索', path: '/programs' },
  { label: '申请服务', path: '/services' },
  { label: '公司介绍', path: '/about' },
  { label: '商业合作', path: '/business-cooperation' },
  { label: '合作院校', path: '/partners' },
  { label: '成功案例', path: '/success-cases' },
  { label: '联系我们', path: '/contact' },
];

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const { openQR, openWeChatQR, closeWeChatQR } = useWeChatQR();
  const theme = useTheme();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  const handleLoginSuccess = (userInfo, token, expiresIn) => {
    setLoginDialogOpen(false);
  };

  const handleLogoutClick = () => {
    // 用户已登出
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const renderDesktopNav = () => (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
      {navigationItems.map((item) => (
        <Button
          key={item.path}
          component={Link}
          to={item.path}
          sx={{
            color: isActivePath(item.path) ? theme.palette.primary.main : theme.palette.text.primary,
            fontWeight: isActivePath(item.path) ? 600 : 400,
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
            },
            position: 'relative',
            '&::after': isActivePath(item.path) ? {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60%',
              height: 2,
              backgroundColor: theme.palette.primary.main,
              borderRadius: 1,
            } : {},
          }}
        >
          {item.label}
        </Button>
      ))}
    </Box>
  );

  const renderMobileDrawer = () => (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={handleDrawerToggle}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          pt: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navigationItems.map((item) => (
          <ListItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              borderLeft: isActivePath(item.path) ? `3px solid ${theme.palette.primary.main}` : 'none',
              backgroundColor: isActivePath(item.path) ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
            }}
          >
            <ListItemText
              primary={item.label}
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: isActivePath(item.path) ? 600 : 400,
                  color: isActivePath(item.path) ? theme.palette.primary.main : theme.palette.text.primary,
                },
              }}
            />
          </ListItem>
        ))}
        {/* 移动端用户状态 */}
        <ListItem sx={{ justifyContent: 'center', py: 2 }}>
          <UserStatus 
            onLoginClick={() => {
              handleDrawerToggle();
              handleLoginClick();
            }}
            onLogoutClick={() => {
              handleDrawerToggle();
              handleLogoutClick();
            }}
          />
        </ListItem>
      </List>
    </Drawer>
  );

  return (
    <>
      <AppBar position="sticky" elevation={0}>
        <Container maxWidth="xl">
          <Toolbar sx={{ px: { xs: 0, sm: 2 } }}>
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'inherit',
                flexGrow: { xs: 1, md: 0 },
                mr: { md: 4 },
              }}
            >
              <SchoolIcon sx={{ mr: 1, fontSize: 32, color: theme.palette.primary.main }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '1.2rem', md: '1.4rem' },
                }}
              >
                希航教育
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ flexGrow: 1 }} />
            {renderDesktopNav()}

            {/* 用户状态或CTA按钮 */}
            <Box sx={{ ml: 2, display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              <UserStatus 
                onLoginClick={handleLoginClick}
                onLogoutClick={handleLogoutClick}
              />
              <Button
                variant="contained"
                color="primary"
                sx={{
                  borderRadius: 20,
                  px: 3,
                }}
                onClick={openWeChatQR}
              >
                免费咨询
              </Button>
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleDrawerToggle}
              sx={{ display: { xs: 'flex', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      {renderMobileDrawer()}

      <WeChatQRDialog 
        open={openQR} 
        onClose={closeWeChatQR}
        title="微信扫码咨询"
      />

      {/* 登录对话框 */}
      <MultiLoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
};

export default Navbar;
